/**
 * Wohin ein Tipp auf eine Benachrichtigung führen soll.
 *
 * **Der Server sagt es schon.** In jeder Push-Nutzlast steht eine Adresse
 * (`Notifier::payload()`): der Reise-Chat, ein Antwortverlauf, die Infoseite.
 * Der Service Worker öffnet sie beim Antippen — und das reicht, solange die
 * App geschlossen war.
 *
 * **Ist sie schon offen, reicht es auf dem iPhone nicht.** Safari kennt
 * `WindowClient.navigate` nicht; der Service Worker holt das Fenster dann nur
 * nach vorn, und es bleibt stehen, wo es war. Deshalb schickt er den Pfad
 * zusätzlich als Nachricht, und hier wird sie aufgefangen.
 *
 * **Keine zweite Wahrheit in der Nutzlast.** Es wäre verlockend, `trip_id` und
 * `with_user` mitzuschicken und den Weg hier zusammenzubauen — dann stünde die
 * Zuordnung „Antwort gehört in den Einzelverlauf" zweimal da, einmal im
 * Notifier und einmal hier, und die nächste Route trüge nur eine der beiden
 * nach. Der Client bekommt einen Pfad und springt dorthin, mehr weiß er nicht.
 */

/** Genau die Kürzung, die auch `api/client.ts` und `lib/push.ts` benutzen. */
const BASE = import.meta.env.BASE_URL.replace(/\/$/, '')

type Empfaenger = (pfad: string) => void

let empfaenger: Empfaenger | null = null

/**
 * Ein Mitschrieb, **weil dreimal Raten genug ist.**
 *
 * Aus dem Betrieb, zum zweiten Mal: „der Absprung aus der Push-Nachricht in
 * die Anfragen funktioniert nicht." Im Labor messen beide Wege grün, und das
 * heißt nicht, dass sie gehen — es heißt, dass das Labor die falsche Frage
 * stellt. Dieselbe Lage wie beim Sendeknopf (`lib/sendeprotokoll.ts`), und
 * dieselbe Antwort: nicht noch eine Vermutung, sondern ein Satz vom Gerät.
 *
 * Aufgeschrieben wird jeder Schritt des Sprungs: was der Worker geschickt hat,
 * was daraus wurde, ob jemand zugehört hat, und was die Nachfrage beim Aufbau
 * ergeben hat. Angesehen wird es in *Einstellungen → Diagnose*.
 *
 * **Nur im Speicher, nichts geht hinaus.** Ein Neustart der Anwendung leert
 * es.
 */
export interface Sprungschritt {
  zeit: string
  was: string
}

const SPRUENGE: Sprungschritt[] = []
const SPRUENGE_MAX = 30

export function merkeSprung(was: string, zeit = new Date().toISOString().slice(11, 23)): void {
  SPRUENGE.push({ zeit, was })
  if (SPRUENGE.length > SPRUENGE_MAX) SPRUENGE.shift()
}

/**
 * A line that repeats does not pile up (1.80.1): the same text as the last
 * entry only moves that entry's time and counts. The asking after coming to
 * the front writes a line every time now -- after a morning of switching
 * apps, thirty of them would have pushed out everything that matters.
 */
function merkeSprungEinmal(was: string): void {
  const zeit = new Date().toISOString().slice(11, 23)
  const letzter = SPRUENGE[SPRUENGE.length - 1]
  const kern = letzter?.was.replace(/ \(\d+×\)$/, '')
  if (letzter !== undefined && kern === was) {
    const n = Number(/ \((\d+)×\)$/.exec(letzter.was)?.[1] ?? '1') + 1
    SPRUENGE[SPRUENGE.length - 1] = { zeit, was: `${was} (${n}×)` }
    return
  }
  merkeSprung(was, zeit)
}

export function sprungVerlauf(): Sprungschritt[] {
  return [...SPRUENGE]
}

/**
 * Wann die App zuletzt nach vorn kam — **eine Zeile, die überschrieben wird.**
 *
 * Aus dem Betrieb kam eine Spur mit acht Push-Nachrichten und keinem einzigen
 * „Angetippt". Daraus liess sich die eine Frage nicht beantworten, für die es
 * den Mitschrieb gibt: **hat niemand getippt, oder kam der Tipp nicht an?**
 * Beides sieht gleich aus, nämlich nach nichts.
 *
 * Der Unterschied ist messbar, und zwar hier: **eine App kommt nicht von
 * selbst nach vorn.** Steht hier ein Zeitpunkt wenige Sekunden nach einer
 * Push-Nachricht und im Worker kein „Angetippt", dann hat jemand getippt und
 * der Worker hat es nicht erfahren.
 *
 * **Überschrieben und nicht angehängt**, und das ist der Grund, warum es nicht
 * einfach eine weitere Zeile im Mitschrieb ist: der fasst dreissig Einträge,
 * und ein Vormittag mit Wechseln zwischen App und Nachrichten hätte alles
 * verdrängt, worauf es ankommt.
 */
let zuletztVorn: string | null = null

export function letzterVordergrund(): string | null {
  return zuletztVorn
}

/**
 * Die Spur des Service Workers — **was dort drin geschah, sieht sonst
 * niemand.**
 *
 * Der Mitschrieb oben beginnt erst, wenn die Anwendung läuft. Alles davor —
 * kam die Benachrichtigung überhaupt mit einer Adresse an, was sah
 * `notificationclick`, hat ein offenes Fenster übernommen — geschieht im
 * Worker, und der hat keine Oberfläche. Also wird er gefragt.
 *
 * **Gelesen und nicht weggenommen:** die Spur ist ein Mitschrieb und keine
 * Nachricht. Wer die Diagnose zweimal ansieht, soll dasselbe sehen.
 */
export type Spurlage = 'kein-worker' | 'keine-antwort' | 'leer' | 'da'

export interface Spurbericht {
  lage: Spurlage
  zeilen: Sprungschritt[]
  /** The version of the worker that answered -- `null` if it did not say. */
  stand: string | null
}

/**
 * **Drei Ausgänge, drei Sätze — und vorher waren es drei Ausgänge und ein
 * Satz.**
 *
 * Hier stand `Promise<Sprungschritt[]>`, und „kein Worker", „keine Antwort"
 * und „wirklich nichts aufgeschrieben" ergaben alle dieselbe leere Liste. Auf
 * der Karte stand dann „Der Service Worker hat nichts aufgeschrieben" — auch
 * dann, wenn er in Wahrheit voll war und bloß nicht rechtzeitig antwortete.
 *
 * **Und rechtzeitig hieß 800 Millisekunden.** Ein Service Worker, den das
 * Betriebssystem angehalten hat, muss erst wieder anlaufen; auf dem iPhone ist
 * das keine Seltenheit, sondern der Normalfall. Ein Werkzeug, das genau dann
 * schweigt, wenn man es braucht, ist schlimmer als keines — es sagt
 * überzeugend das Falsche. Auf einer Diagnoseseite wartet niemand auf eine
 * Zehntelsekunde; hier sind drei Sekunden richtig.
 */
export async function workerSpur(): Promise<Spurbericht> {
  if (!('serviceWorker' in navigator)) return { lage: 'kein-worker', zeilen: [], stand: null }
  try {
    const anmeldung = await navigator.serviceWorker.ready
    const worker = anmeldung.active
    if (worker === null) return { lage: 'kein-worker', zeilen: [], stand: null }
    return await new Promise<Spurbericht>((fertig) => {
      const kanal = new MessageChannel()
      const uhr = window.setTimeout(() => fertig({ lage: 'keine-antwort', zeilen: [], stand: null }), 3000)
      kanal.port1.onmessage = (ereignis) => {
        window.clearTimeout(uhr)
        const daten = ereignis.data as { zeilen?: Sprungschritt[]; stand?: string } | null
        const zeilen = daten?.zeilen ?? []
        fertig({ lage: zeilen.length === 0 ? 'leer' : 'da', zeilen, stand: daten?.stand ?? null })
      }
      worker.postMessage({ typ: 'tb-spur-frage' }, [kanal.port2])
    })
  } catch {
    return { lage: 'keine-antwort', zeilen: [], stand: null }
  }
}


/**
 * Ein Ziel, das ankam, bevor jemand zuhörte.
 *
 * Beim Kaltstart wird das Fenster geöffnet *und* bekommt die Nachricht, lange
 * bevor React steht. Ohne diesen Zettel wäre sie weg, und der Tipp führte auf
 * die Startseite — also genau dorthin, wo man nicht hinwollte.
 *
 * Verbraucht heißt gelöscht: ein späteres Neuaufbauen der Oberfläche soll
 * nicht ein zweites Mal wegspringen.
 */
let liegengeblieben: string | null = null

/**
 * Aus der Adresse einen Pfad für den Router machen — oder `null`.
 *
 * **Fremde Herkunft wird nicht gefolgt.** Die Nachricht kommt zwar vom eigenen
 * Service Worker, aber ein Sprung ist ein Sprung: was nicht von hier ist, hat
 * hier nichts zu suchen.
 *
 * **Und der Präfix muss weg.** `main.tsx` baut den `BrowserRouter` ohne
 * `basename`, während der Server mit `Config::basePath()` präfixt. Ungekürzt
 * landete `/travel/t/7` als unbekannter Pfad bei `<Route path="*">` und damit
 * auf der Startseite.
 */
export function alsPfad(url: string): string | null {
  try {
    const ziel = new URL(url, location.href)
    if (ziel.origin !== location.origin) return null
    let pfad = ziel.pathname
    if (BASE !== '' && pfad.startsWith(BASE)) pfad = pfad.slice(BASE.length)
    return (pfad === '' ? '/' : pfad) + ziel.search
  } catch {
    return null
  }
}

/**
 * Zuhören — **vor dem ersten Zeichnen**, in `main.tsx`.
 *
 * **`startMessages()` ist die Zeile, die man vergisst.** Wer wie hier
 * `addEventListener` statt `onmessage` benutzt, bekommt Nachrichten erst nach
 * diesem Aufruf; bis dahin stapeln sie sich stumm. Damit ist auch die
 * Reihenfolge von Registrierung und Übernahme des Controllers gleichgültig:
 * `client.postMessage()` geht an das Fenster, nicht durch den Controller.
 */
export function beobachteTippziel(): void {
  /*
   * **Womit die App aufgemacht hat — die zweite Hälfte derselben Frage.**
   *
   * Beim Kaltstart aus einer Benachrichtigung soll `openWindow(ziel)` die App
   * an der angetippten Adresse öffnen. Auf dem iPhone startet eine Web-App vom
   * Home-Bildschirm aber an ihrer `start_url`, und dann steht hier `/` statt
   * `/t/1/with/18`. Das ist der Unterschied zwischen „der Sprung ging verloren"
   * und „der Sprung wurde nie ausgelöst", und ohne diese Zeile ist er nicht zu
   * sehen.
   */
  merkeSprung(`Aufgebaut auf ${alsPfad(location.href) ?? location.pathname}`)

  /*
   * **Der Stempel hängt nicht am Service Worker.** Er beantwortet die Frage
   * „kam die App nach vorn?", und die gilt auch auf einem Gerät ohne Worker —
   * dort ist sie sogar die einzige Auskunft, die es gibt.
   */
  const stempeln = () => {
    if (document.visibilityState === 'visible') {
      zuletztVorn = new Date().toISOString().slice(11, 23)
    }
  }
  /*
   * **Beim Aufbau wird nicht gestempelt**, obwohl die App in diesem Augenblick
   * sichtbar ist. Der Kaltstart steht schon eine Zeile weiter oben, mit seiner
   * eigenen Uhrzeit und der Adresse dazu; ihn hier zu wiederholen hiesse, den
   * Fall „zurueck in die App" nie leer zu sehen — und genau dessen Leere ist
   * eine Auskunft.
   */
  document.addEventListener('visibilitychange', stempeln)

  if (!('serviceWorker' in navigator)) return


  navigator.serviceWorker.addEventListener('message', (ereignis) => {
    const daten = ereignis.data as { typ?: string; url?: string; zeit?: string; was?: string; spaet?: boolean } | null

    // The worker's trace, as it happens (1.80.1). If these lines arrive, the
    // worker sees this window; the cache alone could not tell that.
    if (daten?.typ === 'tb-spur' && typeof daten.was === 'string') {
      merkeSprung(`Worker: ${daten.was}`, typeof daten.zeit === 'string' ? daten.zeit : undefined)
      return
    }

    // A push while the app is open (1.82.0) -- the second of two roads.
    if (daten?.typ === 'tb-push') {
      pushGemeldet(daten, 'Worker-Nachricht')
      return
    }

    if (daten?.typ !== 'tb-ziel' || typeof daten.url !== 'string') return

    merkeSprung(`Worker meldet ${daten.url}${daten.spaet === true ? ' (spaet)' : ''}`)
    // **Erst antworten, dann springen.** Der Service Worker wartet eine halbe
    // Sekunde auf ein Lebenszeichen; bleibt es aus, nimmt er den alten Weg.
    // The late delivery comes without a port and needs no answer.
    ereignis.ports[0]?.postMessage({ ok: true })
    springeZu(daten.url, 'Vom Worker gemeldet')
  })
  navigator.serviceWorker.startMessages()

  // The first road for a push while the app is open (1.82.0): it needs no
  // client object, see `meldeDerApp()` in `sw.js`.
  if (typeof BroadcastChannel === 'function') {
    new BroadcastChannel('tb-push').addEventListener('message', (ereignis) => {
      pushGemeldet(ereignis.data, 'BroadcastChannel')
    })
  }
}

/**
 * A push that arrived while the app is open -- shown as the app's own banner
 * (1.82.0, `components/PushBanner.tsx`).
 *
 * **Why the app shows it at all.** Measured on the device with 1.81.0: with
 * the app open, a tap on the system banner reached nobody -- iOS fired no
 * `notificationclick` (a known WebKit fault, iOS 17 and 18). A tap on our own
 * banner is an ordinary tap in the page, and it jumps through `springeZu()`
 * like every other road.
 *
 * Only while the page is visible: a page in the background does not show a
 * banner and does not answer, so the worker leaves the system notification
 * standing. When the chat of the push is already open, there is nothing to
 * jump to; the answer goes anyway, so the system banner disappears.
 */
export interface PushHinweis {
  url: string
  titel: string
  text: string
  /** False when the chat of the push is already open -- then only the lists refresh. */
  zeigen: boolean
}

type PushHoerer = (hinweis: PushHinweis) => void
const pushHoerer = new Set<PushHoerer>()
let letzterPush: { url: string; zeit: number } | null = null
/** In Chromium both roads arrive; one banner per push. */
const PUSH_DOPPELT_MS = 5000

function pushGemeldet(daten: unknown, weg: string): void {
  const d = daten as { typ?: string; url?: unknown; titel?: unknown; text?: unknown } | null
  if (d?.typ !== 'tb-push' || typeof d.url !== 'string') return
  const pfad = alsPfad(d.url)
  if (pfad === null) return
  const jetzt = Date.now()
  if (letzterPush !== null && letzterPush.url === d.url && jetzt - letzterPush.zeit < PUSH_DOPPELT_MS) {
    merkeSprung(`Banner: dieselbe Mitteilung auch über ${weg}`)
    return
  }
  if (document.visibilityState !== 'visible') {
    merkeSprung(`Banner: Push über ${weg}, App nicht sichtbar`)
    return
  }
  letzterPush = { url: d.url, zeit: jetzt }
  const hier = alsPfad(location.href) ?? ''
  const zeigen = pfad.split('?')[0] !== hier.split('?')[0]
  const hinweis: PushHinweis = {
    url: d.url,
    titel: typeof d.titel === 'string' ? d.titel : '',
    text: typeof d.text === 'string' ? d.text : '',
    zeigen,
  }
  if (pushHoerer.size === 0) {
    merkeSprung(`Banner: Push über ${weg}, niemand zeigt ihn`)
    return
  }
  for (const hoerer of pushHoerer) hoerer(hinweis)
  merkeSprung(`Banner: Push über ${weg}, ${zeigen ? 'gezeigt' : 'übergangen (Chat offen)'}`)
  void navigator.serviceWorker?.ready
    .then((anmeldung) => anmeldung.active?.postMessage({ typ: 'tb-push-gesehen', url: d.url }))
    .catch(() => {})
}

/** The banner subscribes here; returns the unsubscribe. */
export function abonnierePush(hoerer: PushHoerer): () => void {
  pushHoerer.add(hoerer)
  return () => {
    pushHoerer.delete(hoerer)
  }
}

/** A tap on the app's own banner -- the same jump as every other road. */
export function bannerAngetippt(url: string): void {
  springeZu(url, 'Banner in der App')
}

/**
 * The last jump, so that one tap jumps once (1.80.1).
 *
 * Since 1.80.1 a tap can arrive on three roads: the worker's call
 * (`tb-ziel`), its late delivery after the app came to the front, and the
 * app's own asking. Before, `tb-ziel` jumped without acknowledging and without
 * checking for a jump already made, and the asking did both -- two copies of
 * the same rule. Now all three go through `springeZu()`.
 */
let letzterSprung: { pfad: string; zeit: number } | null = null
const SPRUNG_GILT_MS = 60_000

/**
 * Jump to a tapped target -- **the one place for all three roads.**
 *
 * - Jumps at most once per address within a minute (the target itself is
 *   valid for a minute in the worker, `ZIEL_GILT_MS`).
 * - Does not jump when the app already stands there. Compared through
 *   `alsPfad()` and not with `location.pathname`: one has the prefix cut
 *   off, the other does not, and under `BASE_PATH` the two would never match.
 *   A `navigate` there would be harmless but a second entry in the history,
 *   and the back button would lead in a circle.
 * - Always acknowledges, so the worker drops the target (1.76.0: it no
 *   longer drops it on being asked).
 *
 * Returns whether the target was dealt with -- jumped, or already there.
 */
function springeZu(url: string, anlass: string): boolean {
  quittiere(url)
  const pfad = alsPfad(url)
  if (pfad === null) {
    merkeSprung(`${anlass}: ${url} ist nicht von hier — nichts getan`)
    return false
  }
  const jetzt = Date.now()
  // Compared by path and not by address (1.81.0): the server's copy of the
  // target and the worker's may differ in how they spell the origin.
  if (letzterSprung !== null && letzterSprung.pfad === pfad && jetzt - letzterSprung.zeit < SPRUNG_GILT_MS) {
    merkeSprung(`${anlass}: ${pfad} — schon gesprungen`)
    return true
  }
  letzterSprung = { pfad, zeit: jetzt }
  if (pfad === alsPfad(location.href)) {
    merkeSprung(`${anlass}: ${pfad} — steht schon da`)
    return true
  }
  if (empfaenger === null) {
    merkeSprung(`${anlass}: niemand hört zu, ${pfad} bleibt liegen`)
    liegengeblieben = pfad
    return true
  }
  merkeSprung(`${anlass}: gesprungen nach ${pfad}`)
  empfaenger(pfad)
  return true
}

/** Tell the worker the target is dealt with; a newer tap in the meantime stays. */
function quittiere(url: string): void {
  void navigator.serviceWorker?.ready
    .then((anmeldung) => anmeldung.active?.postMessage({ typ: 'tb-ziel-erledigt', url }))
    .catch(() => {})
}

/**
 * Der Router meldet sich an. Was liegengeblieben ist, kommt sofort — **und
 * beim ersten Mal wird auch der Worker gefragt.**
 *
 * Der Grund steht in `sw.js` an `ZIELSPEICHER`: beim Kaltstart kann der
 * Verweis in der Adresse verlorengehen, weil eine Web-App vom Home-Bildschirm
 * an ihrer `start_url` startet und nicht an der angetippten Adresse. Dann
 * kommt hier keine Nachricht an, es liegt nichts, und die App steht auf der
 * Startseite. Also wird gefragt.
 */
export function abonniereTippziel(wer: Empfaenger): () => void {
  empfaenger = wer
  if (liegengeblieben !== null) {
    const pfad = liegengeblieben
    liegengeblieben = null
    merkeSprung(`Liegengebliebenes Ziel ${pfad} abgeholt`)
    wer(pfad)
  } else if (!gefragt) {
    gefragt = true
    void frageDenWorker('Beim Aufbau')
  }

  /*
   * **Und jedes Mal, wenn die App nach vorn kommt oder den Fokus bekommt,
   * wird der Worker gefragt** (seit 1.80.1).
   *
   * Auf Android und am Rechner legt der Worker beim Tipp das Ziel ab und
   * erreicht das Fenster meist selbst; die Frage hier holt nach, was ein
   * schlafendes Fenster verpasst hat. Auf iOS sollte seit 1.82.0 `navigate`
   * an der Mitteilung tragen -- **am Gerät gemessen (1.126/1.127) trägt es
   * bei einer App im Hintergrund nicht**, und `notificationclick` kommt dort
   * auch nicht. Seit 1.128.0 antwortet der Worker deshalb auch mit der
   * Mitteilung, die er gezeigt hat und die nicht mehr in der
   * Mitteilungszentrale steht (`verschwundeneMitteilung()` in `sw.js`).
   *
   * 1.81.0 bis 1.82.0 ging daneben eine wartende Frage an den Server
   * (`push_taps`). Am Gerät gemessen: wo sie helfen sollte, löst iOS das
   * Tipp-Ereignis gar nicht aus, und wo das Ereignis läuft, ist der Sprung
   * längst passiert. Seit 1.83.0 ist der Weg weg.
   *
   * **`focus` zusätzlich zu `visibilitychange`**: ob ein Banner-Tipp bei
   * offener App die Sichtbarkeit wechselt oder nur den Fokus, ist am Gerät
   * nicht für jeden Fall belegt. Beide lösen dieselbe Frage aus, und eine
   * laufende wird nicht verdoppelt.
   *
   * **Ohne Tipp findet das nichts**, und das ist keine Nebensache: wer die App
   * bloß wieder aufnimmt, springt nirgendwohin.
   */
  let laeuft = false
  const beimAuftauchen = () => {
    if (document.visibilityState !== 'visible' || laeuft) return
    laeuft = true
    void frageDenWorker('Beim Nach-vorn-Kommen').finally(() => {
      laeuft = false
    })
  }
  document.addEventListener('visibilitychange', beimAuftauchen)
  window.addEventListener('focus', beimAuftauchen)

  return () => {
    document.removeEventListener('visibilitychange', beimAuftauchen)
    window.removeEventListener('focus', beimAuftauchen)
    if (empfaenger === wer) empfaenger = null
  }
}

let gefragt = false

/** How long a question to the worker waits for a late answer (#120). */
const SPAET_MS = 15_000

/**
 * Beim Worker nachfragen, ob ein angetippter Verweis für uns bereitliegt.
 * Returns whether it found one and dealt with it.
 *
 * **Mit Zeitschranke**, aus demselben Grund wie `fragWegen()` in der anderen
 * Richtung: im Browser kann ein alter Worker stehen, der diese Frage nicht
 * kennt. Bleibt die Antwort aus, war es eben keine.
 */
async function frageDenWorker(anlass: string, versuch = 1): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false
  try {
    const anmeldung = await navigator.serviceWorker.ready
    const worker = anmeldung.active
    if (worker === null) return false

    // Where the worker found it (1.128.0): laid down by a tap, or the
    // notification that vanished without one. Said in the trace, so the
    // device shows which road jumped.
    let quelle: string | null = null
    const url = await new Promise<string | null | 'zu-spaet'>((fertig) => {
      const kanal = new MessageChannel()
      let vorbei = false
      /*
       * **Zweieinhalb Sekunden und nicht 800 Millisekunden.** Ein angehaltener
       * Service Worker muss erst anlaufen; kommt die Antwort zu spät, findet
       * die App das Ziel nie und steht auf der Startseite -- der Fehler, den
       * das hier gerade verhindern soll.
       */
      const uhr = window.setTimeout(() => {
        vorbei = true
        fertig('zu-spaet')
      }, 2500)
      /*
       * **And an answer after the limit still counts** (1.94.0, #120). The
       * limit only decides when to ask again; until here it also threw the
       * answer away. A worker that took longer than three questions -- a
       * cold start under load -- answered into nothing, and the app stood on
       * the start page with the target still lying in the worker. Measured
       * with the worker's door `frageVerzug` in `kaltstartsprung.mjs`. A late
       * answer goes through `springeZu()` like any other, which jumps once
       * per address; the port listens for SPAET_MS and then closes.
       */
      const schluss = window.setTimeout(() => kanal.port1.close(), SPAET_MS)
      kanal.port1.onmessage = (ereignis) => {
        window.clearTimeout(uhr)
        window.clearTimeout(schluss)
        kanal.port1.close()
        const daten = ereignis.data as { url?: string | null; quelle?: string | null } | null
        const antwort = daten?.url ?? null
        quelle = daten?.quelle ?? null
        if (!vorbei) {
          fertig(antwort)
          return
        }
        if (antwort !== null) springeZu(antwort, `${anlass} gefragt, Antwort nach der Frist`)
      }
      worker.postMessage({ typ: 'tb-ziel-frage' }, [kanal.port2])
    })

    /*
     * **Zu spät ist nicht leer** (1.76.0). Beim Kaltstart war „zu spät" der
     * Fall, um den es ging: ein angehaltener Worker antwortete nach der Frist.
     * Seit der Worker das Ziel beim Fragen nicht mehr wegnimmt, liegt es noch
     * da -- also wird beim Aufbau noch zweimal gefragt. Beim Nach-vorn-Kommen
     * nicht: dort wartet daneben die Frage an den Server.
     */
    if (url === 'zu-spaet') {
      if (anlass === 'Beim Aufbau' && versuch < 3) {
        merkeSprung(`${anlass} gefragt: der Worker antwortete nicht rechtzeitig, frage noch einmal`)
        return frageDenWorker(anlass, versuch + 1)
      }
      merkeSprungEinmal(`${anlass} gefragt: der Worker antwortete nicht rechtzeitig`)
      return false
    }
    if (url === null) {
      // The empty case is written only at build time; after coming to the
      // front the round in `abonniereTippziel()` writes it once, if at all.
      if (anlass === 'Beim Aufbau') merkeSprung(`${anlass} gefragt: nichts lag bereit`)
      return false
    }
    return springeZu(url, quelle === 'verschwunden' ? `${anlass} gefragt, verschwundene Mitteilung` : `${anlass} gefragt`)
  } catch {
    merkeSprungEinmal(`${anlass} gefragt: der Worker antwortete nicht`)
    return false
  }
}
